import React, { useEffect, useState } from 'react';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  Typography,
  Box,
  Grid,
  Avatar,
  Chip,
  Stack,
  IconButton,
  Tooltip,
  useMediaQuery,
  useTheme,
} from '@mui/material';
import { format, parse } from 'date-fns';
import CloseIcon from '@mui/icons-material/Close';
import CheckCircleIcon from '@mui/icons-material/CheckCircle';
import ArrowBackIosIcon from '@mui/icons-material/ArrowBackIos';
import ArrowForwardIosIcon from '@mui/icons-material/ArrowForwardIos';
import LaunchIcon from '@mui/icons-material/Launch';
import PostItem from '../posts/PostItem';
import './WelcomeScreen.css';

const ForcePostDialog = ({ open, onClose, forcePostsData, onMarkPostRead }) => {
  const [forcePosts, setForcePosts] = useState([]);
  const [currentPostIndex, setCurrentPostIndex] = useState(0);
  // Track read posts by post_receiver_id
  const [readPosts, setReadPosts] = useState(new Set());
  const [isLoading, setIsLoading] = useState(false);
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('md'));

  useEffect(() => {
    if (open) {

      if (forcePostsData && forcePostsData.length > 0) {
        // Initialize posts and mark already read ones
        const initialPosts = forcePostsData.map(post => ({
          ...post,
          // Ensure read_flag is properly set
          read_flag: post.read_flag || 'N'
        }));

        setForcePosts(initialPosts);
        setCurrentPostIndex(0);

        // Initialize readPosts set with already read posts
        const alreadyRead = new Set();
        initialPosts.forEach(post => {
          if (post.read_flag === 'R' && post.post_receiver_id) {
            alreadyRead.add(post.post_receiver_id);
          }
        });
        setReadPosts(alreadyRead);

        setIsLoading(false);
      } else {
        setForcePosts([]);
        setIsLoading(false);
        onClose(); // No force posts, proceed
      }
    }
  }, [open, forcePostsData, onClose]);

  // Mark a single forced post (identified by post_receiver_id) as read
  const markPostAsRead = async (postReceiverId) => {
    try {

      setReadPosts(prev => {
        const newSet = new Set(prev);
        newSet.add(postReceiverId);
        return newSet;
      });

      // Update the specific post's read_flag in forcePosts
      setForcePosts(prevPosts => prevPosts.map(post =>
        post.post_receiver_id === postReceiverId
          ? { ...post, read_flag: 'R' }
          : post
      ));

      // Call the API
      const success = await onMarkPostRead(postReceiverId);
      if (!success) {
        console.error('Failed to mark post as read via API');
        // If API call fails, revert the local state
        setReadPosts(prev => {
          const newSet = new Set(prev);
          newSet.delete(postReceiverId);
          return newSet;
        });

        setForcePosts(prevPosts => prevPosts.map(post =>
          post.post_receiver_id === postReceiverId
            ? { ...post, read_flag: 'N' }
            : post
        ));
      }
      return success;
    } catch (error) {
      console.error('Mark post as read error:', error);
      // Revert the local state on error
      setReadPosts(prev => {
        const newSet = new Set(prev);
        newSet.delete(postReceiverId);
        return newSet;
      });

      setForcePosts(prevPosts => prevPosts.map(post =>
        post.post_receiver_id === postReceiverId
          ? { ...post, read_flag: 'N' }
          : post
      ));
      return false;
    }
  };

  // Handle automatic read when blue overlay is clicked
  const handlePostRead = (postReceiverId) => {
    if (postReceiverId && !readPosts.has(postReceiverId)) {
      markPostAsRead(postReceiverId);
    }
  };

  const handleNext = () => {
    if (currentPostIndex < forcePosts.length - 1) {
      setCurrentPostIndex(currentPostIndex + 1);
    }
  };

  const handlePrevious = () => {
    if (currentPostIndex > 0) {
      setCurrentPostIndex(currentPostIndex - 1);
    }
  };

  const handleMoveToApp = () => {
    // Check if all posts are read before closing
    const allPostsRead = forcePosts.every(post =>
      readPosts.has(post.post_receiver_id) || post.read_flag === 'R'
    );

    if (allPostsRead) {
      onClose();
    } else {
    }
  };

  const handleClose = () => {
    // Only allow closing if all posts are read
    const allPostsRead = forcePosts.every(post =>
      readPosts.has(post.post_receiver_id) || post.read_flag === 'R'
    );

    if (allPostsRead) {
      onClose();
    } else {
    }
  };

  if (isLoading) {
    return (
      <Dialog open={open} maxWidth={isMobile ? 'xs' : 'md'} fullWidth fullScreen={isMobile}>
        <DialogContent>
          <Box display="flex" justifyContent="center" alignItems="center" py={4}>
            <div className="loading-spinner">Loading...</div>
          </Box>
        </DialogContent>
      </Dialog>
    );
  }

  if (forcePosts.length === 0) {
    return null;
  }

  const currentPost = forcePosts[currentPostIndex];
  const currentPostReceiverId = currentPost?.post_receiver_id;
  const isCurrentPostRead = currentPostReceiverId
    ? readPosts.has(currentPostReceiverId) || currentPost.read_flag === 'R'
    : false;

  const isLastPost = currentPostIndex === forcePosts.length - 1;

  // Check if all posts are read
  const allPostsRead = forcePosts.every(post =>
    readPosts.has(post.post_receiver_id) || post.read_flag === 'R'
  );

  return (
    <Dialog
      open={open}
      maxWidth={isMobile ? 'xs' : 'md'}
      fullWidth
      fullScreen={isMobile}
      PaperProps={{
        className: 'welcome-screen-backdrop',
        style: isMobile ? {
          margin: 0,
          maxHeight: '100%',
          height: '100%',
        } : {},
      }}
    >
      <DialogTitle sx={{ padding: isMobile ? 1 : 2 }}>
        <Box display="flex" justifyContent="space-between" alignItems="center">
          <Typography variant={isMobile ? 'h6' : 'h6'} component="div" sx={{ fontSize: isMobile ? '1.1rem' : '1.25rem' }}>
            Important Announcement ({currentPostIndex + 1} of {forcePosts.length})
          </Typography>
          <Box>
            <IconButton
              onClick={handleClose}
              disabled={!allPostsRead}
              size="small"
              sx={{
                color: allPostsRead ? 'inherit' : 'grey.500',
                '&:disabled': {
                  color: 'grey.500',
                }
              }}
              title={allPostsRead ? "Close" : "Read all posts to continue"}
            >
              <CloseIcon />
            </IconButton>
          </Box>
        </Box>
      </DialogTitle>

      <DialogContent className="force-post-content" sx={{ padding: isMobile ? 1 : 2, maxHeight: isMobile ? 'calc(100vh - 200px)' : '60vh' }}>
        <Stack spacing={2}>
          <PostItem
            data={{
              ...currentPost,
              read_flag: isCurrentPostRead ? 'R' : (currentPost.read_flag || 'N')
            }}
            showbutton={false}
            showshare={false}
            showButtonT={false}
            callfrom="force"
            addRead={(postReceiverId) => {
              if (postReceiverId && !isCurrentPostRead) {
                handlePostRead(postReceiverId);
              }
            }}
          />

          {/* Read status indicator or instruction */}
          {isCurrentPostRead ? (
            <Box display="flex" alignItems="center" justifyContent="center" sx={{ mt: 2 }}>
              <CheckCircleIcon color="success" sx={{ mr: 1 }} />
              <Typography variant="body2" color="success.main">
                Post marked as read
              </Typography>
            </Box>
          ) : (
            <Box display="flex" alignItems="center" justifyContent="center" sx={{ mt: 2 }}>
              <Typography variant="body2" color="text.secondary" sx={{ fontStyle: 'italic' }}>
                Please read the post above to continue
              </Typography>
            </Box>
          )}
        </Stack>
      </DialogContent>

      <DialogActions className="force-post-actions" sx={{
        flexDirection: 'row',
        padding: isMobile ? 1 : 2,
        justifyContent: 'space-between',
        alignItems: 'center',
      }}>
        {/* Previous Post Button */}
        <IconButton
          onClick={handlePrevious}
          disabled={currentPostIndex === 0}
          size={isMobile ? 'large' : 'medium'}
          sx={{
            backgroundColor: currentPostIndex === 0 ? 'transparent' : 'primary.main',
            color: currentPostIndex === 0 ? 'disabled' : 'white',
            '&:hover': {
              backgroundColor: currentPostIndex === 0 ? 'transparent' : 'primary.dark',
            },
            '&:disabled': {
              backgroundColor: 'transparent',
              color: 'action.disabled',
            }
          }}
        >
          <ArrowBackIosIcon />
        </IconButton>

        {/* Post counter and read status */}
        <Box sx={{ textAlign: 'center', flex: 1 }}>
          <Typography variant="body2" color="text.primary">
            {currentPostIndex + 1} of {forcePosts.length}
          </Typography>
          {isCurrentPostRead && (
            <Box display="flex" alignItems="center" justifyContent="center" sx={{ mt: 0.5 }}>
              <CheckCircleIcon color="success" sx={{ fontSize: '1rem', mr: 0.5 }} />
              <Typography variant="caption" color="success.main">
                Read
              </Typography>
            </Box>
          )}
        </Box>

        {!isLastPost ? (
          <IconButton
            onClick={handleNext}
            disabled={!isCurrentPostRead}
            size={isMobile ? 'large' : 'medium'}
            sx={{
              backgroundColor: isCurrentPostRead ? 'primary.main' : 'grey.300',
              color: isCurrentPostRead ? 'white' : 'grey.500',
              '&:hover': {
                backgroundColor: isCurrentPostRead ? 'primary.dark' : 'grey.300',
              },
              '&:disabled': {
                backgroundColor: 'grey.300',
                color: 'grey.500',
              }
            }}
            title={isCurrentPostRead ? "Next Post" : "Read current post to continue"}
          >
            <ArrowForwardIosIcon />
          </IconButton>
        ) : (
          <IconButton
            onClick={handleMoveToApp}
            disabled={!allPostsRead}
            size={isMobile ? 'large' : 'medium'}
            sx={{
              backgroundColor: allPostsRead ? 'success.main' : 'grey.300',
              color: allPostsRead ? 'white' : 'grey.500',
              '&:hover': {
                backgroundColor: allPostsRead ? 'success.dark' : 'grey.300',
              },
              '&:disabled': {
                backgroundColor: 'grey.300',
                color: 'grey.500',
              }
            }}
            title={allPostsRead ? "Move to App" : "Read all posts to continue"}
          >
            <LaunchIcon />
          </IconButton>
        )}
      </DialogActions>
    </Dialog>
  );
};

export default ForcePostDialog;